import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Theme context — toggles between "dark" (default) and "light".
 * Persists per-user in localStorage. Reflects on the <html> element as
 * `data-theme="light"` / `data-theme="dark"`, which our CSS override layer
 * in `index.css` keys off of to repaint the canvas, cards, borders, and text
 * without touching individual components.
 */
const ThemeContext = createContext(null);

const STORAGE_KEY = "vorion_theme";

function readInitialTheme() {
  if (typeof window === "undefined") return "dark";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return "dark"; // default for existing users
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readInitialTheme);

  // Apply to <html data-theme="..."> + persist on every change.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore quota */ }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle, isDark: theme === "dark", isLight: theme === "light" }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
